import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ServiceStatus } from '@/models/enums/service';
import { mergeObjects } from '@/utils/utils';

import Service from '@/services';
import { AdvertisingItemModel, AdvertisingTypeModel } from '@/models/classes/advertising';

const service = new Service.AdvertisingService();

// Interface
interface AdvertisingState {
    types: {
        network: {
            code: ServiceStatus;
            error?: string;
        };
        data: AdvertisingTypeModel[];
    };
    list: {
        network: {
            code: ServiceStatus;
            error?: string;
        };
        data: AdvertisingItemModel[];
        total: number;
    };
}

// Initialize State
const initialState: AdvertisingState = {
    types: {
        network: {
            code: ServiceStatus.idle,
            error: undefined,
        },
        data: [],
    },
    list: {
        network: {
            code: ServiceStatus.idle,
            error: undefined,
        },
        data: [],
        total: 0,
    },
};

// Reducer
export const slice = createSlice({
    name: 'Advertising',
    initialState,
    reducers: {
        reset: () => initialState,
        patch: (state, action) => mergeObjects({ ...state }, action.payload),
    },
    extraReducers: (builder) => {
        // Fetch Types
        builder.addCase(onFetchTypes.pending, (state) => {
            state.types.network.code = ServiceStatus.loading;
        });
        builder.addCase(onFetchTypes.fulfilled, (state, action) => {
            state.types.network.code = ServiceStatus.succeeded;
            if (action.payload != null) {
                state.types.data = action.payload;
            }
        });
        builder.addCase(onFetchTypes.rejected, (state, action) => {
            state.types.network.code = ServiceStatus.failed;
            state.types.network.error = (action.payload as string) || 'Failed to fetch types';
        });

        // Fetch List
        builder.addCase(onFetchList.pending, (state) => {
            state.list.network.code = ServiceStatus.loading;
        });
        builder.addCase(onFetchList.fulfilled, (state, action) => {
            state.list.network.code = ServiceStatus.succeeded;
            if (action.payload != null) {
                state.list.data = action.payload.data;
                state.list.total = action.payload.total;
            }
        });
        builder.addCase(onFetchList.rejected, (state, action) => {
            state.list.network.code = ServiceStatus.failed;
            state.list.network.error = (action.payload as string) || 'Failed to fetch list';
        });

        // Load More List
        builder.addCase(onLoadMoreList.pending, (state) => { });
        builder.addCase(onLoadMoreList.fulfilled, (state, action) => {
            if (action.payload != null) {
                state.list.data.push(...action.payload.data);
                state.list.total = action.payload.total;
            }
        });
        builder.addCase(onLoadMoreList.rejected, (state, action) => { });
    },
});

// Async Thunks
export const onFetchTypes = createAsyncThunk('advertising/fetch/types', async (_, thunkAPI) => {
    try {
        const apiData = await service.fetchTypes();
        return apiData;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error?.response?.data?.message || 'Failed to fetch types');
    }
});

export const onFetchList = createAsyncThunk('advertising/fetch/list', async (param: { advertising: number; limit: number; offset: number }, thunkAPI) => {
    try {
        const apiData = await service.fetchList(param.advertising, param.limit, param.offset);
        return apiData;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error?.response?.data?.message || 'Failed to fetch list');
    }
});

export const onLoadMoreList = createAsyncThunk('advertising/loadmore/list', async (param: { advertising: number; limit: number; offset: number }, thunkAPI) => {
    try {
        const apiData = await service.fetchList(param.advertising, param.limit, param.offset);
        return apiData;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error?.response?.data?.message || 'Failed to load more');
    }
});

// Actions
export const { reset, patch } = slice.actions;

// Export
export default slice.reducer;
